import type { Source } from "@types"

/**
 * Variables d’alarme « composées » au niveau bâtiment : pas de clé dédiée dans
 * `monitoring.firstdate` ; on s’appuie sur les compteurs et des variables sous-jacentes.
 */
const BUILDING_COMPOSITE_ALARM_VARIABLES = {
    energiepardju_elec_tout: ["energie_elec_tout"] as const,
    energiepardju_gaz_tout: ["energie_gaz_tout"] as const,
    energie_tout_tout: [
        "energie_elec_tout",
        "energie_gaz_tout",
        "energie_chaleur_tout",
    ] as const,
} as const

type BuildingCompositeKey = keyof typeof BUILDING_COMPOSITE_ALARM_VARIABLES

function isBuildingCompositeVariable(
    variableType: string
): variableType is BuildingCompositeKey {
    return variableType in BUILDING_COMPOSITE_ALARM_VARIABLES
}

/**
 * Indique si une entrée existe dans `monitoring.firstdate` pour une variable
 * (au moins une fréquence renseignée).
 *
 * @param monitoring bloc monitoring de la source
 * @param variableKey identifiant de variable (ex. `energie_elec_tout`)
 */
export function hasMonitoringFirstDateEntry(
    monitoring: Source["monitoring"] | undefined,
    variableKey: string
): boolean {
    const entry = monitoring?.firstdate?.[variableKey]
    if (!entry || typeof entry !== "object") return false
    return Object.values(entry).some(
        (v) => v != null && String(v).trim() !== ""
    )
}

/**
 * Source : une variable d’alarme n’est proposée que si elle apparaît dans
 * `monitoring.firstdate` pour cette source.
 *
 * @param variableType type `ConfigVariable`
 * @param source source courante
 */
export function shouldIncludeAlarmVariableForSource(
    variableType: string,
    source: Source | undefined
): boolean {
    if (!source) return false
    return hasMonitoringFirstDateEntry(source.monitoring, variableType)
}

/**
 * Bâtiment : union des `firstdate` sur toutes les sources rattachées ; pour les
 * variables composées, uniquement les compteurs et les clés sous-jacentes définies ci-dessus.
 *
 * @param variableType type `ConfigVariable`
 * @param buildingSources toutes les sources du bâtiment (compteurs, IoT, météo, etc.)
 * @param meterSources sous-ensemble compteurs (types `METER_TYPES_LIST`)
 */
export function shouldIncludeAlarmVariableForBuilding(
    variableType: string,
    buildingSources: Source[],
    meterSources: Source[]
): boolean {
    if (isBuildingCompositeVariable(variableType)) {
        const keys = BUILDING_COMPOSITE_ALARM_VARIABLES[variableType]
        return meterSources.some((s) =>
            keys.some((key) => hasMonitoringFirstDateEntry(s.monitoring, key))
        )
    }

    return buildingSources.some((s) =>
        hasMonitoringFirstDateEntry(s.monitoring, variableType)
    )
}
